import { afterRenderEffect, Component, computed, effect, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessengerService } from '../../../services/messenger/messenger.service';
import { environment } from '../../../../environments/environment';
import { PlayerPanel } from "../../../components/users/player-panel/player-panel";
import { Message } from "../../../components/chat/message/message";
import { ExternalLinkModal } from "../../../components/modals/external-link-modal/external-link-modal";
import { FormsModule } from '@angular/forms';
import { GiphyService } from '../../../services/giphy/giphy.service';
import { EmojiPicker } from '../../../components/chat/emoji-picker/emoji-picker';
import { SettingsService } from '../../../services/settings/settings.service';

@Component({
  selector: 'app-pager',
  imports: [PlayerPanel, Message, FormsModule, ExternalLinkModal, EmojiPicker],
  templateUrl: './pager.html',
  styleUrl: './pager.scss',
})
export class Pager {
  private messenger = inject(MessengerService);
  private http = inject(HttpClient);
  public giphy = inject(GiphyService);
  private settingsService = inject(SettingsService);

  @ViewChild('msgEl')
  private chatMessagesEl!: ElementRef<HTMLDivElement>;
  @ViewChild('bottomAnchor')
  private bottomAnchor!: ElementRef<HTMLDivElement>;

  public showGiphy = signal(false);
  public showEmoji = signal(false);
  public showCelebration = signal(false);
  public showBirthday = signal(false);
  public showSnow = signal(false);
  public showFireworks = signal(false);
  public showMobileSidebar = signal(false);
  private celebrationTimeout: any;
  private birthdayTimeout: any;
  private snowTimeout: any;
  private fireworksTimeout: any;
  private lastProcessedMessageGuid = '';

  private lastConversationId: string = '';
  private hasScrolledToBottomOnLoad = false;
  private wasNearBottom = false;
  private hasSetLoadListener = false;

  private readonly scrollEffect = afterRenderEffect(() => {
    const currentConversations = this.conversations();
    const conv = this.selectedConversation();
    const currentMessages = conv ? conv.messages : [];

    if (!this.chatMessagesEl) return;

    const el = this.chatMessagesEl.nativeElement;
    const anchor = this.bottomAnchor?.nativeElement;
    if (!el || !anchor) return;

    // Attach capturing load listener once to capture image load events
    if (!this.hasSetLoadListener) {
      this.hasSetLoadListener = true;
      el.addEventListener('load', (event) => {
        if (event.target instanceof HTMLImageElement) {
          const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
          if (this.wasNearBottom && dist <= 400) {
            anchor.scrollIntoView({ block: 'end', behavior: 'auto' });
          }
        }
      }, true);
    }

    // Reset initial scroll flag if the active conversation changes
    const currentConvId = conv?.friendUserId || '';
    if (currentConvId !== this.lastConversationId) {
      this.lastConversationId = currentConvId;
      this.hasScrolledToBottomOnLoad = false;
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    if (!this.hasScrolledToBottomOnLoad && currentMessages.length > 0) {
      anchor.scrollIntoView({ block: 'end', behavior: 'auto' });
      this.hasScrolledToBottomOnLoad = true;
      this.wasNearBottom = true;
    } else if (distanceFromBottom <= 100) {
      anchor.scrollIntoView({ block: 'end', behavior: 'auto' });
      this.wasNearBottom = true;
    } else {
      this.wasNearBottom = false;
    }
  });

  public conversations = this.messenger.conversations;
  public selectedConversation = computed(() => {
    const id = this.messenger.activeConversationId();
    if (!id) return null;
    return this.conversations().find(c => c.friendUserId === id) || null;
  });
  public newMessage = signal('');

  getStatusColor(status: any) {
    switch (status) {
      case 1: return 'text-success'; // online
      case 2: return 'text-danger'; // afk/away
      case 3: return 'text-warning'; // busy
      case 4: return 'text-primary'; // in-game
      case 5: return 'text-warning'; // ready
      default: return 'text-secondary';
    }
  }

  getStatusText(status: any) {
    switch (status) {
      case 1: return 'online'; // online
      case 2: return 'away'; // afk
      case 3: return 'busy'; // busy
      case 4: return 'ingame'; // in-game
      case 5: return 'ready'; // ready
      default: return 'offline';
    }
  }

  constructor() {
    effect(() => {
      const id = this.messenger.activeConversationId();
      if (id) {
        this.messenger.markAsRead(id);
      }
    });

    effect(() => {
      const conv = this.selectedConversation();
      if (conv && conv.messages && conv.messages.length > 0) {
        const lastMsg = conv.messages[conv.messages.length - 1];
        const identifier = lastMsg.guid || lastMsg.timestamp?.toString() || lastMsg.text;

        if (identifier && identifier !== this.lastProcessedMessageGuid) {
          const isInitial = !this.lastProcessedMessageGuid;
          this.lastProcessedMessageGuid = identifier;

          if (!isInitial && lastMsg.text) {
            const text = lastMsg.text.toLowerCase();
            const celebrationWords = ['congrats', 'congratulations', 'grats', 'gratz', 'hurray', 'hell yeah'];
            const snowWords = ['merry christmas', 'snowing'];

            if (celebrationWords.some(word => text.includes(word))) {
              this.triggerCelebration();
            }
            if (snowWords.some(word => text.includes(word))) {
              this.triggerSnow();
            }
            if (text.includes('happy birthday')) {
              this.triggerBirthday();
            }
            if (text.includes('boom')) {
              this.triggerFireworks();
            }
          }
        }
      } else {
        this.lastProcessedMessageGuid = '';
      }
    });
  }

  public select(conversation: any) {
    this.messenger.activeConversationId.set(conversation.friendUserId);
    this.showMobileSidebar.set(false);
  }

  public close(event: Event, friendUserId: string) {
    event.stopPropagation();
    event.preventDefault();
    this.messenger.closeConversation(friendUserId);
  }

  public giphySearch(query: string) {
    if (query.length > 2) {
      this.giphy.search(query);
    }
  }

  public selectGif(gif: any) {
    const attachment = {
      title: gif.title || 'Giphy',
      url: gif.images.fixed_height.url
    };
    this.send('', attachment);
    this.showGiphy.set(false);
  }

  public toggleEmoji() {
    this.showEmoji.update(s => !s);
    if (this.showEmoji()) {
      this.showGiphy.set(false);
    }
  }

  public toggleGiphy() {
    this.showGiphy.update(s => !s);
    if (this.showGiphy()) {
      this.showEmoji.set(false);
    }
  }

  public insertEmoji(emoji: string, inputEl: HTMLInputElement) {
    const startPos = inputEl.selectionStart ?? inputEl.value.length;
    const endPos = inputEl.selectionEnd ?? inputEl.value.length;
    const originalValue = this.newMessage();

    const newValue = originalValue.substring(0, startPos) + emoji + originalValue.substring(endPos);
    this.newMessage.set(newValue);

    setTimeout(() => {
      inputEl.focus();
      const newCursorPos = startPos + emoji.length;
      inputEl.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }

  /**
   * Send a message out to a user.
   * @param msg 
   * @param attachment
   * @returns 
   */
  public send(msg: string, attachment?: any) {
    if ((!msg || msg.length == 0) && !attachment)
      return;

    const now = new Date();
    const dateString = now.toString();

    let message: any = {
      toUserId: this.selectedConversation().friendUserId,
      fromUser: {
        userId: this.messenger.currentUser().userId,
        userName: this.messenger.currentUser().name,
        portraitId: this.messenger.currentUser().portraitId,
        userNameColor: "#FFFFFF"
      },
      classes: "Chartreuse",
      color: this.settingsService.fontColor(),
      text: msg,
      attachment: attachment,
      isMe: true,
      timestamp: dateString
    };

    // Linkify detection
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const match = msg.match(urlRegex);
    if (match) {
      const url = match[0];
      this.http.post(environment.platformUrl + 'api/chat/linkify', {
        url: url,
        title: "",
        description: "",
        image: ""
      }).subscribe({
        next: (res: any) => {
          if (res.image && typeof res.image === 'string') {
            res.image = [`data:image/png;base64,${res.image}`];
          }
          message.linkPreview = res;
          this.messenger.conversations.set([...this.messenger.conversations()]);
        },
        error: (err) => console.error("Linkify failed", err)
      });
    }

    let outgoingMesage = {
      toUserId: this.selectedConversation().friendUserId,
      text: msg,
      attachment: attachment,
      color: this.settingsService.fontColor(),
      isMe: true
    };


    this.selectedConversation().messages.push(message);
    this.messenger.conversations.set([...this.messenger.conversations()]);

    console.log("My message is => ", message);
    this.messenger.sendMessage(outgoingMesage);
    this.newMessage.set('');
  }

  public triggerCelebration() {
    if (this.celebrationTimeout) {
      clearTimeout(this.celebrationTimeout);
    }
    this.showCelebration.set(false);
    setTimeout(() => {
      this.showCelebration.set(true);
      this.celebrationTimeout = setTimeout(() => {
        this.showCelebration.set(false);
      }, 5000);
    }, 10);
  }

  public triggerBirthday() {
    if (this.birthdayTimeout) {
      clearTimeout(this.birthdayTimeout);
    }
    this.showBirthday.set(false);
    setTimeout(() => {
      this.showBirthday.set(true);
      this.birthdayTimeout = setTimeout(() => {
        this.showBirthday.set(false);
      }, 5000);
    }, 10);
  }

  public triggerSnow() {
    if (this.snowTimeout) {
      clearTimeout(this.snowTimeout);
    }
    this.showSnow.set(false);
    setTimeout(() => {
      this.showSnow.set(true);
      this.snowTimeout = setTimeout(() => {
        this.showSnow.set(false);
      }, 5000);
    }, 10);
  }

  public triggerFireworks() {
    if (this.fireworksTimeout) {
      clearTimeout(this.fireworksTimeout);
    }
    this.showFireworks.set(false);
    setTimeout(() => {
      this.showFireworks.set(true);
      this.fireworksTimeout = setTimeout(() => {
        this.showFireworks.set(false);
      }, 6000);
    }, 10);
  }
}
